import { Alert, Card, cn } from "@breenova/ui";
import { Baby, ChevronLeft, ChevronRight, Egg as EggIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { DetailHeader } from "@/components/app-shell";
import { useBreedingCalendar, type CalendarEntry } from "@/lib/breeding";

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"] as const;

/**
 * The dated things a breeder has to be present for, as a month.
 *
 * A month first, because the question a breeder brings here is "when is the busy week",
 * and a grid with a dot per job answers it at a glance where a list of forty dated rows
 * does not. Terracotta dots are ringing days — the one job that cannot slip — and sage
 * dots are hatches. Tapping a day lists its jobs underneath, drawn the way Today draws
 * them, so arriving here from Today is not arriving at a different app.
 */
export function BreedingCalendarPage() {
  const calendar = useBreedingCalendar();
  const today = new Date().toISOString().slice(0, 10);

  const [month, setMonth] = useState(() => today.slice(0, 7));
  const [selected, setSelected] = useState(today);

  const entries = useMemo(() => calendar.data?.entries ?? [], [calendar.data?.entries]);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEntry[]>();

    for (const entry of entries) {
      map.set(entry.on, [...(map.get(entry.on) ?? []), entry]);
    }

    return map;
  }, [entries]);

  /*
   * The jobs under the grid. Today also carries whatever is late — an overdue ringing
   * is today's job, whatever date it was first due on.
   */
  const agenda = useMemo(
    () =>
      entries
        .filter((entry) =>
          selected === today ? entry.on === today || entry.isOverdue : entry.on === selected,
        )
        .sort((a, b) => a.daysAway - b.daysAway),
    [entries, selected, today],
  );

  return (
    <div className="flex flex-col">
      <DetailHeader backTo="/breeding" title="Calendar" size="large" />

      <section className="flex flex-col gap-4 px-5 pb-lg md:px-gutter-wide" aria-live="polite">
        {calendar.isError ? <Alert tone="error">Could not load the calendar.</Alert> : null}

        <Card className="flex flex-col gap-2.5 px-3 py-4 md:max-w-form">
          <div className="flex items-center justify-between px-1">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setMonth(shiftMonth(month, -1))}
              className="flex size-9.5 items-center justify-center rounded-full bg-surface-container text-on-surface transition-colors hover:bg-surface-container-high"
            >
              <ChevronLeft className="size-4.5" aria-hidden />
            </button>

            <span className="text-section-header text-on-surface" data-numeric>
              {monthLabel(month)}
            </span>

            <button
              type="button"
              aria-label="Next month"
              onClick={() => setMonth(shiftMonth(month, 1))}
              className="flex size-9.5 items-center justify-center rounded-full bg-surface-container text-on-surface transition-colors hover:bg-surface-container-high"
            >
              <ChevronRight className="size-4.5" aria-hidden />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-y-1 text-center" role="grid" aria-label={monthLabel(month)}>
            {WEEKDAYS.map((weekday) => (
              <span key={weekday} className="text-weekday uppercase text-on-surface-variant" role="columnheader">
                {weekday}
              </span>
            ))}

            {monthCells(month).map((day, index) => {
              if (!day) {
                return <span key={`blank-${index}`} aria-hidden />;
              }

              const jobs = byDay.get(day) ?? [];
              const ringing = jobs.some((job) => job.kind === "Ring");
              const hatching = jobs.some((job) => job.kind !== "Ring");
              const isToday = day === today;
              const isSelected = day === selected;

              return (
                <button
                  key={day}
                  type="button"
                  role="gridcell"
                  aria-selected={isSelected}
                  aria-label={`${longDay(day)}${jobs.length > 0 ? `, ${jobs.length} ${jobs.length === 1 ? "job" : "jobs"}` : ""}`}
                  onClick={() => setSelected(day)}
                  className="flex h-10.5 flex-col items-center justify-center gap-0.75"
                >
                  <span
                    className={cn(
                      "flex h-7.5 w-8.5 items-center justify-center rounded-full text-day",
                      isToday
                        ? "bg-on-surface font-bold text-surface"
                        : isSelected
                          ? "bg-primary-fixed text-on-primary-fixed"
                          : "text-on-surface",
                    )}
                    data-numeric
                  >
                    {Number(day.slice(8))}
                  </span>
                  <span aria-hidden className="flex h-1.25 gap-0.75">
                    <span className={cn("size-1.25 rounded-full", ringing ? "bg-action" : "bg-transparent")} />
                    <span className={cn("size-1.25 rounded-full", hatching ? "bg-primary" : "bg-transparent")} />
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex gap-4 px-1.5 text-micro font-semibold text-on-surface-body">
            <span className="flex items-center gap-1.5">
              <span aria-hidden className="size-2 rounded-full bg-action" />
              Ringing
            </span>
            <span className="flex items-center gap-1.5">
              <span aria-hidden className="size-2 rounded-full bg-primary" />
              Hatch
            </span>
          </div>
        </Card>

        <h2 className="text-section-header text-on-surface" data-numeric>
          {longDay(selected)}
        </h2>

        {calendar.isPending ? (
          <p className="text-body text-on-surface-variant">Loading the calendar…</p>
        ) : agenda.length === 0 ? (
          <p className="text-body text-on-surface-variant">
            Nothing due. Dates appear once a nest has its first egg.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            {agenda.map((entry, index) => (
              <li key={`${entry.nestId}-${entry.kind}-${entry.chickId ?? index}`}>
                <CalendarRow entry={entry} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function CalendarRow({ entry }: { entry: CalendarEntry }) {
  const ringing = entry.kind === "Ring";
  const late = entry.isOverdue;

  return (
    <Link
      to={`/breeding/nests/${entry.nestId}`}
      className={cn(
        "flex items-center gap-3 rounded-card bg-surface-container-lowest p-3 shadow-card",
        "transition-colors hover:bg-surface-container-low",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-full",
          late
            ? "bg-secondary-fixed text-on-secondary-fixed-variant"
            : "bg-primary-fixed text-on-primary-fixed-variant",
        )}
      >
        {ringing ? <Baby className="size-4.5" /> : <EggIcon className="size-4.5" />}
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="truncate text-label text-on-surface">
          {ringing ? "Ring the chicks" : "Hatch due"}
        </span>
        <span
          className={cn("truncate text-caption", late ? "text-secondary" : "text-on-surface-variant")}
          data-numeric
        >
          {entry.locationCode ? `Cage ${entry.locationCode} · ` : ""}round {entry.round}
          {ringing ? "" : ` · ${entry.eggCount} eggs`}
          {late
            ? ` · ${Math.abs(entry.daysAway)} ${Math.abs(entry.daysAway) === 1 ? "day" : "days"} late`
            : ""}
          {/* A mule pairing is worth calling out: every chick from it is a hybrid. */}
          {entry.isHybridPair ? " · mule pair" : ""}
        </span>
      </span>
    </Link>
  );
}

/** "2026-10" moved by whole months. */
function shiftMonth(month: string, by: number): string {
  const [year, index] = month.split("-").map(Number);
  const moved = new Date(Date.UTC(year!, index! - 1 + by, 1));

  return moved.toISOString().slice(0, 7);
}

/**
 * The days of a month laid out Monday-first, with blanks before the first and after the
 * last so every row is a whole week.
 */
function monthCells(month: string): (string | null)[] {
  const [year, index] = month.split("-").map(Number);
  const first = new Date(Date.UTC(year!, index! - 1, 1));
  const days = new Date(Date.UTC(year!, index!, 0)).getUTCDate();
  const lead = (first.getUTCDay() + 6) % 7;

  const cells: (string | null)[] = Array.from({ length: lead }, () => null);

  for (let day = 1; day <= days; day += 1) {
    cells.push(`${month}-${String(day).padStart(2, "0")}`);
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  return cells;
}

function monthLabel(month: string): string {
  return new Date(`${month}-01T00:00:00Z`).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "Friday 2 October". Stored UTC, read in the breeder's own calendar. */
function longDay(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}
